import OwnedByDropdown from '@client/Applications/DataGrids/Grids/CRM/OwnedByDropdown';
import TagsEditDropdown from '@client/CommonComponents/ClientTags/TagsEditDropdown';
import { UserPermissionsContext } from '@client/Context/UserPermissions';
import {
    AsyncDropDownPaginated,
    DropDown,
    LogLevel,
    lookupValidator,
    OptionTypeBase,
    PendingButton,
    SearchQuery,
    showBanner,
    Sisp,
    SortOrder,
} from '@sprint/sprint-react-components';
import _ from 'lodash';
import React, { BaseSyntheticEvent, FunctionComponent, useContext, useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { DictionaryContext, RepositoryFactoryContext } from '../..';
import { ucwords } from '../../../../Helpers/StringHelper';
import { AllCountriesRequest } from '../../Api/AllCountriesRequest';
import { OrganisationsRequest } from '../../Api/OrganisationsRequest';
import CustomPropertyForm, { AvailablePropertyTypes } from '../CustomProperties/CustomPropertyForm';

interface Props {
    shown: boolean;
    onClose: () => void;
    onSuccess: (event: any) => Promise<boolean>;
}

type FormData = {
    id: number;
    organisation_name: string;
    organisation_sector: string;
    email?: string;
    telephone: string;
    website: string;
    edu_data_package?: string;
    type_name: string;
    address1: string;
    address2: string;
    address3: string;
    address4: string;
    county: string;
    region: string;
    postcode: string;
    country: string;
    owned_by: OptionTypeBase;
    tag_ids?: number[];
};

const OrganisationsAddSisp: FunctionComponent<Props> = (props: Props) => {
    // Repositories
    const organisationsRepository = useContext(RepositoryFactoryContext).getApiRepository(new OrganisationsRequest());
    const countryRepository = useContext(RepositoryFactoryContext).getApiRepository(new AllCountriesRequest());

    const dictionary = useContext(DictionaryContext);
    const permissions = useContext(UserPermissionsContext);
    const customTagsEnabled = permissions.organisationsCustomTags.isEnabled;

    // State: Tags
    const [tags, setTags] = useState<{ value: number; label: any }[]>();

    const customProperties: any = JSON.parse(
        String((document.getElementById('custom-properties') as HTMLInputElement).value),
    );

    const focusRef = useRef<HTMLInputElement>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);

    // Form states
    const {
        control,
        register,
        handleSubmit,
        setValue,
        reset,
        watch,
        setError,
        formState: { errors, isSubmitting },
    } = useForm<FormData>({ mode: 'all' });

    const [sector, setSector] = useState<OptionTypeBase>();
    const [country, setCountry] = useState<OptionTypeBase>();
    const [customPropertyValues, setCustomPropertyValues] = useState<any>();
    const newEmail = watch('email');

    useEffect(() => {
        if (props.shown) {
            focusRef.current?.focus();
            // Reset the form reset trigger whenever the sisp is opened.
        } else {
            resetForm();
        }
    }, [props.shown]);

    const resetForm = () => {
        setSector(undefined);
        setCountry(undefined);
        setCustomPropertyValues(undefined);
        setSubmitting(false);
        reset();
    };

    const validate = async (): Promise<boolean> => {
        let emailValid = true;

        const emailValidator = lookupValidator('email', 'text');
        emailValid = emailValidator.validate(newEmail ?? '');

        if (emailValid && newEmail) {
            emailValid = await checkEmailIsUnique(newEmail ?? '');
            if (!emailValid) {
                setError(
                    'email',
                    { type: 'validate', message: 'Email is already used by another organisation' },
                    { shouldFocus: true },
                );
            }
        }

        const sectorValid = sector !== undefined;

        const newValidationState = {
            sector: sectorValid,
            email: emailValid,
        };
        return _.every(newValidationState);
    };

    const checkEmailIsUnique = async (email: string): Promise<boolean> => {
        return organisationsRepository
            .post_action('check_email', undefined, { email: email })
            .then((results: any) => {
                return results.data.result;
            })
            .catch((err) => {
                return false;
            });
    };

    const handleAddRow = async (data: FieldValues, event?: BaseSyntheticEvent<unknown, any, any>): Promise<boolean> => {
        return organisationsRepository
            .create({ ...data, owned_by: data.owned_by?.value })
            .then((results: any) => {
                props.onSuccess([results.data]);
                showBanner({
                    message: ucwords(dictionary['organisation']) + ' created successfully',
                    level: LogLevel.SUCCESS,
                });
                const buttonClicked = (event?.nativeEvent as SubmitEvent | undefined)?.submitter;
                if (buttonClicked?.dataset.redirectTo) {
                    window.location.href = `/subscribers/organisations/view/${results.data.id}`;
                }
                return true;
            })
            .catch((err) => {
                showBanner({
                    message: 'Failed to create contact - ' + (err?.message ?? err),
                    level: LogLevel.ERROR,
                });
                return false;
            });
    };

    const onSubmitForm = async (data: FieldValues, event?: BaseSyntheticEvent<unknown, any, any>) => {
        event?.preventDefault();
        setSubmitting(true);
        if ((await validate()) && (await handleAddRow(data, event))) {
            resetForm();
            props.onClose();
        } else {
            setSubmitting(false);
        }
    };

    const loadCountries = (filter?: string, page?: number): Promise<{ value: number; label: JSX.Element }[]> => {
        // Fetch all users for client account
        const query = new SearchQuery(page ?? 1, 100, 'id', SortOrder.ASC, filter);
        return countryRepository
            .search(query)
            .then((results) => {
                return _.map(
                    _.filter(results.results as any[], (country: OptionTypeBase) => {
                        return _.includes(country?.label?.toLowerCase(), filter?.toLowerCase());
                    }),
                    (country: any) => {
                        return country;
                    },
                );
            })
            .catch((err: any) => {
                showBanner({
                    message: 'Failed to get countries - ' + (err?.message ?? err),
                });
                return [];
            });
    };

    const setCustomPropertyValue = (key: any, value: any) => {
        setValue(key, value);
    };

    return (
        <Sisp
            isOpen={props.shown}
            onCancel={() => {
                props.onClose();
            }}
            footerOverride={
                <>
                    <PendingButton pending={submitting} type="submit" form="organisation_add_form">
                        Save
                    </PendingButton>
                    <PendingButton
                        redirect={true}
                        pending={submitting}
                        type="submit"
                        form="organisation_add_form"
                        variant="default"
                    >
                        Save & View
                    </PendingButton>
                </>
            }
        >
            <h4>Add an {ucwords(dictionary['organisation'])}</h4>
            <Form id="organisation_add_form" onSubmit={handleSubmit(onSubmitForm)}>
                <Form.Group>
                    <Form.Control type="hidden" {...register('id')} />
                </Form.Group>
                <Form.Group>
                    <Form.Control type="hidden" {...register('edu_data_package')} />
                </Form.Group>
                <Form.Group>
                    <Form.Label>Name</Form.Label>
                    <Form.Control autoComplete="off" {...register('organisation_name')} />
                </Form.Group>

                <Form.Group>
                    <Form.Label>Sector</Form.Label>
                    <DropDown
                        value={sector}
                        isInvalid={sector === undefined}
                        isClearable={false}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        classNamePrefix={'sector-dropdown'}
                        onChange={(selected: OptionTypeBase) => {
                            setSector(selected);
                            setValue('organisation_sector', selected?.value ?? '');
                        }}
                        options={[
                            { value: 'Business', label: 'Business' },
                            { value: 'School', label: 'School' },
                        ]}
                    />
                </Form.Group>

                <Form.Group>
                    <Form.Label>{ucwords(dictionary['organisation'])} Type</Form.Label>
                    <Form.Control {...register('type_name')} />
                </Form.Group>

                <Form.Group>
                    <Form.Label>Address</Form.Label>
                    <Form.Control {...register('address1')} placeholder={'Address 1'} />
                </Form.Group>
                <Form.Group>
                    <Form.Control {...register('address2')} placeholder={'Address 2'} />
                </Form.Group>
                <Form.Group>
                    <Form.Control {...register('address3')} placeholder={'Address 3'} />
                </Form.Group>
                <Form.Group>
                    <Form.Control {...register('address4')} placeholder={'Address 4'} />
                </Form.Group>
                <Form.Group>
                    <Form.Control {...register('county')} placeholder={'County'} />
                </Form.Group>
                <Form.Group>
                    <Form.Control {...register('region')} placeholder={'Region'} />
                </Form.Group>
                <Form.Group>
                    <Form.Control {...register('postcode')} placeholder={ucwords(dictionary['postcode'])} />
                </Form.Group>

                <Form.Group>
                    <Form.Label>Country</Form.Label>
                    <AsyncDropDownPaginated
                        id={'country'}
                        value={country}
                        isInvalid={false}
                        isClearable={true}
                        menuPlacement="auto"
                        menuPosition="fixed"
                        menuPortalTarget={document.body}
                        classNamePrefix={'country-dropdown'}
                        onChange={(selected: OptionTypeBase) => {
                            setCountry(selected);
                            setValue('country', selected?.value ?? '');
                        }}
                        loadOptions={async (filter: string, _loadedOptions, { page }) => {
                            const res = await loadCountries(filter, page);
                            return {
                                options: res,
                                hasMore: false,
                                additional: {
                                    page: page + 1,
                                },
                            };
                        }}
                    />
                </Form.Group>

                <Form.Group>
                    <Controller
                        control={control}
                        name={'owned_by'}
                        render={({ field: { value, onChange } }) => (
                            <OwnedByDropdown
                                label="Owned By"
                                onChange={onChange}
                                value={value}
                                defaultToYou={true}
                                isClearable={false}
                            />
                        )}
                    />
                </Form.Group>

                <Form.Group className={errors.email ? 'has-error' : ''}>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                        autoComplete="off"
                        {...register('email', {
                            pattern: {
                                value: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
                                message: 'This email address is not valid.',
                            },
                        })}
                    />
                    {errors.email && <span className="help-block">{errors.email.message}</span>}
                </Form.Group>

                <Form.Group>
                    <Form.Label>Telephone</Form.Label>
                    <Form.Control {...register('telephone')} />
                </Form.Group>

                <Form.Group>
                    <Form.Label>Website</Form.Label>
                    <Form.Control {...register('website')} />
                </Form.Group>

                {customTagsEnabled && (
                    <TagsEditDropdown
                        onChange={(tags) => {
                            setTags(tags);
                            setValue(
                                'tag_ids',
                                tags.map((v) => v.value),
                            );
                        }}
                        existingTags={tags}
                    />
                )}

                <CustomPropertyForm
                    propertyType={AvailablePropertyTypes.organisations}
                    customProperties={customProperties}
                    customPropertyValues={customPropertyValues}
                    updateFormPropertyState={setCustomPropertyValues}
                    setPropertyValue={setCustomPropertyValue}
                />
            </Form>
        </Sisp>
    );
};

export default OrganisationsAddSisp;
